import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampPuces } from "@/composants/ui/champ";
import { Vide } from "@/composants/ui/etats";
import { euros, NATURES_FRAIS, natureFrais } from "@/lib/libelles";
import {
  api,
  type FicheCommandeFournisseur,
  type FraisFournisseur,
  type NatureFrais,
} from "@/lib/api";

type Agir = (geste: () => Promise<FicheCommandeFournisseur>) => Promise<boolean>;

/**
 * Les frais d'une commande fournisseur (LOT 2, ticket #40) : transport,
 * emballage ou autre, portés par la commande entière. La base les impute aux
 * commandes client servies au prorata du montant HT de leurs lignes ; l'écran
 * lit la part de chacune, en euros, sans rien recalculer. Une part attend les
 * prix tant qu'une ligne commandée n'en a pas ; annulée, la commande n'impute rien.
 *
 * Porter des frais est un geste sur l'ensemble (`frais_ajoutables`), les retirer
 * se fait en préparation seulement (`frais_retirables`). Sans la permission de
 * voir les montants, la fiche n'en porte aucun, et la carte ne s'affiche pas.
 */
export function FraisDeLaCommande({
  fiche,
  peutAgir,
  agir,
}: {
  fiche: FicheCommandeFournisseur;
  peutAgir: boolean;
  agir: Agir;
}) {
  const [ouvert, setOuvert] = useState(false);
  if (fiche.frais === null) return null;
  const peutPorter = peutAgir && fiche.frais_ajoutables;
  const peutRetirer = peutAgir && fiche.frais_retirables;

  return (
    <Carte>
      <EnTeteCarte className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-encre">Frais fournisseur</h2>
        {peutPorter && !ouvert ? (
          <Bouton taille="compact" variante="discret" onClick={() => setOuvert(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Porter des frais
          </Bouton>
        ) : null}
      </EnTeteCarte>
      {ouvert ? (
        <CorpsCarte className="border-b border-filet">
          <FormulaireFrais
            apresEnvoi={fiche.statut !== "BROUILLON"}
            onAnnuler={() => setOuvert(false)}
            onValider={async (saisie) => {
              if (await agir(() => api.porterFrais(fiche.id, saisie))) setOuvert(false);
            }}
          />
        </CorpsCarte>
      ) : null}
      {fiche.frais.length === 0 ? (
        <Vide
          titre="Aucun frais"
          detail="Transport, emballage : portés par la commande entière, ils se répartissent entre les commandes client au prorata de leurs lignes."
        />
      ) : (
        <ul className="divide-y divide-filet">
          {fiche.frais.map((frais) => (
            <li key={frais.id} className="flex flex-col gap-2 px-4 py-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="min-w-0 flex-1">
                  <span className="font-medium text-encre">{natureFrais(frais.nature)}</span>
                  <span className="text-encre-douce"> — {frais.libelle}</span>
                </p>
                <span className="tabular-nums text-encre">{montantDit(frais)}</span>
              </div>
              {frais.motif_ajout !== null ? (
                <p className="text-encre-douce">Ajoutés après l'envoi : {frais.motif_ajout}</p>
              ) : null}
              <Parts frais={frais} />
              {peutRetirer ? (
                <span>
                  <Bouton
                    taille="compact"
                    variante="discret"
                    aria-label={`Retirer les frais : ${frais.libelle}`}
                    onClick={() => agir(() => api.retirerFrais(fiche.id, frais.id))}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    Retirer
                  </Bouton>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Carte>
  );
}

/**
 * Le montant d'un frais, en toutes lettres. Ajoutés après l'envoi (ticket #66), ils ne
 * comptent qu'une fois leur écart de frais accepté : jusqu'alors, « en attente de décision ».
 */
function montantDit(frais: FraisFournisseur) {
  if (frais.montant_demande_ht === null) {
    return frais.montant_retenu_ht === null
      ? `${euros(frais.montant_ht)} HT`
      : `Portés ${euros(frais.montant_ht)}, retenus ${euros(frais.montant_retenu_ht)} HT`;
  }
  const demande = `${euros(frais.montant_demande_ht)} HT demandés`;
  switch (frais.statut_ecart) {
    case "ACCEPTE":
      return `${demande}, acceptés : ${euros(frais.montant_retenu_ht ?? frais.montant_demande_ht)} HT retenus`;
    case "CONTESTE":
      return `${demande}, contestés : ils ne comptent pas`;
    case "RESORBE":
    case "DETACHE":
      return `${demande}, remplacés par un AR : voir l'écart`;
    default:
      return `${demande}, en attente de décision : ils ne comptent pas encore`;
  }
}

function Parts({ frais }: { frais: FraisFournisseur }) {
  // Des frais ajoutés après l'envoi n'ont pas de part tant qu'aucun montant n'est retenu.
  if (frais.parts.length === 0) return null;
  if (frais.montant_demande_ht !== null && frais.montant_retenu_ht === null) return null;
  return (
    <dl aria-label={`Part imputée : ${frais.libelle}`} className="flex flex-col gap-1">
      {frais.parts.map((part) => (
        <div key={part.commande_client_id} className="flex flex-wrap justify-between gap-2">
          <dt className="text-encre-douce">
            Part de <span className="font-mono">{part.numero_commande_client}</span>
          </dt>
          <dd className="tabular-nums text-encre">
            {part.part_ht === null
              ? "En attente des prix : une ligne commandée n'en a pas"
              : euros(part.part_ht)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function FormulaireFrais({
  apresEnvoi,
  onAnnuler,
  onValider,
}: {
  /** La commande est partie : le motif est exigé, et les frais attendent une décision. */
  apresEnvoi: boolean;
  onAnnuler: () => void;
  onValider: (saisie: {
    nature: NatureFrais;
    libelle: string;
    montant_ht: number;
    motif?: string;
  }) => Promise<void>;
}) {
  const [nature, setNature] = useState<NatureFrais | "">("");
  const [libelle, setLibelle] = useState("");
  const [montant, setMontant] = useState("");
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const montantLu = Number(montant.replace(",", ".").trim());
  const complet =
    nature !== "" &&
    libelle.trim() !== "" &&
    montant.trim() !== "" &&
    montantLu > 0 &&
    (!apresEnvoi || motif.trim() !== "");
  return (
    <form
      aria-label="Porter des frais"
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        if (!complet) return;
        setEnvoi(true);
        await onValider({
          nature,
          libelle: libelle.trim(),
          montant_ht: montantLu,
          ...(apresEnvoi ? { motif: motif.trim() } : {}),
        });
        setEnvoi(false);
      }}
    >
      <ChampPuces
        libelle="Nature"
        required
        options={Object.entries(NATURES_FRAIS).map(([valeur, libelle]) => ({ valeur, libelle }))}
        valeur={nature}
        onChanger={(valeur) => setNature(valeur as NatureFrais)}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          libelle="Libellé"
          required
          value={libelle}
          onChange={(e) => setLibelle(e.target.value)}
        />
        <Champ
          libelle="Montant HT (€)"
          required
          inputMode="decimal"
          value={montant}
          onChange={(e) => setMontant(e.target.value)}
        />
      </div>
      {apresEnvoi ? (
        <>
          <Champ
            libelle="Motif de l'ajout"
            required
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
          <p className="text-encre-douce">
            La commande est partie : ces frais ne compteront dans le coût d'achat qu'une fois leur
            écart de frais accepté.
          </p>
        </>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !complet}>
          Porter les frais
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
